import { auth, getAPI } from "./firebase";
import type { LoyaltyBusiness } from "./contexts/CustomerContext";
import { getAccountConflict } from "./utils/accountConflict";
import { READ_TIMEOUT_MS, withRetry } from "./utils/network";
import {
  GlobalCustomer,
  CustomerOrderingGetOrderStatusReq,
  CustomerOrderingGetOrderStatusRes,
  CustomerOrderingSendOrderReq,
  CustomerOrderingSendOrderRes,
  LoyaltyEntry,
  LocationCustomer,
  FormDefinition,
} from "@sally-pos/pos-core";
import {
  CustomerOrderingGetMenuReq,
  CustomerOrderingGetMenuRes,
} from "./types/extensions";
import {
  ConfirmPaymentRequest,
  ConfirmPaymentResponse,
  InitiatePaymentRequest,
  InitiatePaymentResponse,
} from "./types/payments";

// TODO: move birthday + customFields into LocationCustomer in pos-core
export type FullLocationCustomer = LocationCustomer & {
  birthday?: string;
  customFields?: Record<string, string>;
};

/**
 * A read callable: capped well below the 70s callable default and repeated on a
 * transient failure, because asking the same question twice is always safe.
 * Writes deliberately don't get this — see utils/network.
 */
const readAPI = <TReq = unknown, TRes = unknown>(
  name: string,
  req: TReq,
  timeout = READ_TIMEOUT_MS,
) => {
  const call = getAPI<TReq, TRes>(name, { timeout });
  return withRetry(() => call(req).then((res) => res.data));
};

interface ICheckIfUserExistsReq {
  phoneNumber: string;
}

export const checkIfUserExists = async (req: ICheckIfUserExistsReq) => {
  return readAPI<ICheckIfUserExistsReq, any>("user-checkIfExists", req);
};

/**
 * A big menu from a cold server could take over the 10s read cap, and each
 * timeout threw the answer away and asked again: one slow load became two.
 */
const MENU_TIMEOUT_MS = 30_000;

export const getMenu = async (req: CustomerOrderingGetMenuReq) => {
  return readAPI<CustomerOrderingGetMenuReq, CustomerOrderingGetMenuRes>(
    "location-menu-get",
    req,
    MENU_TIMEOUT_MS,
  );
};

/**
 * Single attempt on purpose: `useOrderProgress` already re-asks every 5s and
 * holds the last stage through a failure, so a retry here would only be a
 * second loop inside the first.
 */
export const getOrderStatus = async (
  data: CustomerOrderingGetOrderStatusReq,
) => {
  const getStatusFunc = getAPI<
    CustomerOrderingGetOrderStatusReq,
    CustomerOrderingGetOrderStatusRes
  >("location-order-getStatus", { timeout: READ_TIMEOUT_MS });

  return getStatusFunc(data).then((res) => res.data);
};

/**
 * Never retried and never given a shorter timeout: the server mints the order
 * id, so a lost reply is indistinguishable from a request that never landed,
 * and a second attempt would place the order twice.
 */
export const sendExternalOrder = async (data: CustomerOrderingSendOrderReq) => {
  const addFunc = getAPI<
    CustomerOrderingSendOrderReq,
    CustomerOrderingSendOrderRes
  >("location-order-send");

  const res = await addFunc(data).then((res) => res.data);

  return res;
};

// ─── Pay now ──────────────────────────────────────────────────────────────────

const CONFIRM_TIMEOUT_MS = 30_000;
/** Creating a checkout is two quick provider calls; don't spin for the 70s default. */
const INITIATE_TIMEOUT_MS = 30_000;

/**
 * Starts a payment attempt. Single shot like `sendExternalOrder` — but here a
 * lost reply costs nothing: the attempt only becomes an order once it is paid,
 * and a retry opens a fresh, separate attempt.
 */
export const initiateOnlinePayment = async (data: InitiatePaymentRequest) =>
  getAPI<InitiatePaymentRequest, InitiatePaymentResponse>(
    "payments-initiate",
    { timeout: INITIATE_TIMEOUT_MS },
  )(data).then((res) => res.data);

/**
 * Asks the server to verify a payment with the provider (and, once paid, hand
 * the order to the venue). Idempotent, so it may be repeated — but a paid
 * order can wait ~8s on the venue's till, so it gets a longer timeout than a
 * plain read. One attempt: like `getOrderStatus`, it is mostly polled, and a
 * retry loop inside the poll would only stretch each round.
 */
export const confirmOnlinePayment = (data: ConfirmPaymentRequest) =>
  getAPI<ConfirmPaymentRequest, ConfirmPaymentResponse>("payments-confirm", {
    timeout: CONFIRM_TIMEOUT_MS,
  })(data).then((res) => res.data);

// ─── Customer portal ──────────────────────────────────────────────────────────

export interface LocationInfo {
  name: string;
  address?: string;
  city?: string;
  country?: string;
  logoURL?: string;
  coverURL?: string;
  [key: string]: unknown;
}

export interface LocationSettings {
  locationId: string;
  customerId: string | null;
  location: LocationInfo | null;
  existingCustomer: FullLocationCustomer | null;
  signupForm: FormDefinition;
}

export const getSettings = async (locationId: string) => {
  console.debug("[api] getSettings →", { locationId });
  const res = await readAPI("location-getSettings", { locationId });
  console.debug("[api] getSettings ←", res);
  return res as LocationSettings;
};

export const signup = async (data: Record<string, any>) => {
  console.debug("[api] signup →", data);
  try {
    const res = await getAPI("user-signup")(data).then((r) => r.data);
    console.debug("[api] signup ←", res);
    return res as { customerId: string };
  } catch (e) {
    // This phone/email's profile belongs to another login: the one signed in
    // is a stray, so end it here for every caller. They only route the
    // customer to sign in (see utils/accountConflict).
    const conflict = getAccountConflict(e);
    if (conflict) {
      console.warn("[api] signup ← profile is on another login", conflict);
      await auth.signOut();
    }
    throw e;
  }
};

export interface ProfileResponse {
  /**
   * The person's identity across every location, or null when this signed-in
   * user has no loyalty profile yet. Shape is what `user-get` returns:
   * `{ id, ...globalCustomer }`.
   */
  customer: (GlobalCustomer & { id: string }) | null;
  locations: unknown[];
  /** Absent from servers that predate business-wide loyalty. */
  loyaltyBusinesses?: LoyaltyBusiness[];
}

export const getProfile = async (
  payload: { locationId?: string } = {},
): Promise<ProfileResponse> => {
  console.debug("[api] getProfile →", payload);

  try {
    const res = (await readAPI("user-get", payload)) as ProfileResponse;
    console.debug("[api] getProfile ←", res);
    return res;
  } catch (e) {
    // Transitional: the callable used to answer "no profile yet" with
    // `not-found` (an HTTP 404). It now returns a null customer, so this branch
    // only catches a browser still talking to the old function mid-deploy, and
    // can be deleted once that has rolled out. Normalising here keeps exactly
    // one shape for every caller.
    if ((e as { code?: string })?.code?.includes("not-found")) {
      console.debug("[api] getProfile ← no profile (legacy not-found)");
      return { customer: null, locations: [] };
    }
    throw e;
  }
};

export interface UpdateProfilePayload {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthday?: string;
}

export const updateProfile = async (data: UpdateProfilePayload) => {
  console.debug("[api] updateProfile →", data);
  const res = await getAPI("user-update")(data).then((r) => r.data);
  console.debug("[api] updateProfile ←", res);
  return res as { success: boolean };
};

export const unsubscribeFromLocation = async (locationId: string) => {
  const res = await getAPI("user-unsubscribe")({ locationId }).then(
    (r) => r.data,
  );
  return res as { success: boolean };
};

export const getChecks = async (locationId: string) => {
  console.debug("[api] getChecks →", { locationId });
  const res = await readAPI("location-getChecks", { locationId });
  console.debug("[api] getChecks ←", res);
  return res as { checks: any[] };
};

export const getCheck = async (locationId: string, checkId: string) => {
  console.debug("[api] getCheck →", { locationId, checkId });
  const res = await readAPI("location-getCheck", { locationId, checkId });
  console.debug("[api] getCheck ←", res);
  return res as { check: any };
};

export const getReceipt = async (locationId: string, checkId: string) => {
  console.debug("[api] getReceipt →", { locationId, checkId });
  const res = await readAPI("location-getReceipt", { locationId, checkId });
  console.debug("[api] getReceipt ←", res);
  return res as { check: any; location: { name: string; currency: string } };
};

export const getLoyaltyEntries = async (locationId: string) => {
  console.debug("[api] getLoyaltyEntries →", { locationId });
  const res = await readAPI("location-getLoyaltyEntries", { locationId });
  console.debug("[api] getLoyaltyEntries ←", res);
  return res as { entries: LoyaltyEntry[] };
};
